import { marked, Renderer, type TokensList } from 'marked';
import { localizeMarkdownHrefs } from '@shared/locales';
import { safeHref, safeImageSrc } from './safe-url';

export interface ParsedMarkdown {
  data: Record<string, string>;
  body: string;
}

/**
 * Frontmatter parser for the flat `key: value` frontmatter this site uses.
 * Values may be bare, single- or double-quoted, and may be folded over
 * several indented lines or written as a `>`/`|` block scalar — the shapes a
 * YAML writer (the CMS) produces for long strings. Nothing nested: the
 * schemas keep frontmatter flat.
 */
export function parseFrontmatter(raw: string): ParsedMarkdown {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(raw);
  if (!match) return { data: {}, body: raw };

  const entries: Array<{ key: string; first: string; more: string[] }> = [];
  for (const line of match[1]!.split(/\r?\n/)) {
    const current = entries[entries.length - 1];
    if (/^\s/.test(line) && current) {
      current.more.push(line.trim());
      continue;
    }
    if (line.trim() === '' && current) {
      current.more.push('');
      continue;
    }
    const separator = line.indexOf(':');
    if (separator === -1 || line.trimStart().startsWith('#')) continue;
    const key = line.slice(0, separator).trim();
    if (key) entries.push({ key, first: line.slice(separator + 1).trim(), more: [] });
  }

  const data: Record<string, string> = {};
  for (const { key, first, more } of entries) {
    while (more.length && more[more.length - 1] === '') more.pop();
    let value: string;
    if (/^[>|][-+]?$/.test(first)) {
      // Block scalar: `|` keeps line breaks, `>` folds lines into spaces.
      value = first.startsWith('|') ? more.join('\n') : more.join(' ').replace(/ {2,}/g, ' ');
    } else {
      value = [first, ...more]
        .filter((part, index) => index === 0 || part !== '')
        .join(' ')
        .trim();
    }
    if (
      (value.startsWith("'") && value.endsWith("'") && value.length >= 2) ||
      (value.startsWith('"') && value.endsWith('"') && value.length >= 2)
    ) {
      const quote = value[0];
      value = value.slice(1, -1);
      value = quote === "'" ? value.replaceAll("''", "'") : value.replaceAll('\\"', '"');
    }
    data[key] = value.trim();
  }
  return { data, body: raw.slice(match[0].length) };
}

const escapeHtml = (value: string) =>
  value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');

/**
 * Raw HTML in content is never passed through: it is shown as text. Content is
 * checked by the tests and the build, but the CMS makes editing easy and a stray
 * `<script>` should not depend on a reviewer noticing it.
 */
const renderer = new Renderer();
renderer.html = ({ text }) => escapeHtml(text);
renderer.link = function ({ href, title, tokens }) {
  const inner = this.parser.parseInline(tokens);
  // Decoded, control-stripped and allowlisted (see ./safe-url.ts); otherwise '#'.
  const safe = safeHref(href) ?? '#';
  const titleAttribute = title ? ` title="${escapeHtml(title)}"` : '';
  const external = /^https?:/i.test(safe) ? ' rel="noopener noreferrer"' : '';
  return `<a href="${escapeHtml(safe)}"${titleAttribute}${external}>${inner}</a>`;
};
renderer.image = ({ href, title, text }) => {
  // Only local images (see "Screenshots" in README.md); anything else is dropped.
  const src = safeImageSrc(href);
  if (!src) return escapeHtml(text);
  const titleAttribute = title ? ` title="${escapeHtml(title)}"` : '';
  return `<img src="${escapeHtml(src)}" alt="${escapeHtml(text)}"${titleAttribute} loading="lazy" decoding="async" />`;
};

/** HTML comments are authoring notes (placeholders, screenshot slots); they never reach a reader. */
export function stripComments(markdown: string): string {
  return markdown.replace(/<!--[\s\S]*?-->/g, '');
}

const TABLE_LABEL: Record<string, string> = {
  en: 'Table (scrolls sideways)',
  es: 'Tabla (se desplaza horizontalmente)',
};

/**
 * Markdown → HTML for `locale`: root-relative links gain the locale prefix.
 * Tables are wrapped in a focusable, labelled region so a keyboard user can
 * scroll a wide table on a narrow screen.
 */
export function renderMarkdown(markdown: string, locale: string): string {
  const html = marked.parse(localizeMarkdownHrefs(stripComments(markdown), locale), { async: false, renderer });
  const label = TABLE_LABEL[locale] ?? TABLE_LABEL.en!;
  return html
    .replaceAll('<table>', `<div class="table-scroll" role="region" tabindex="0" aria-label="${label}"><table>`)
    .replaceAll('</table>', '</table></div>');
}

// Link text with up to one level of nested brackets (e.g. an image inside a link).
const LINK_TEXT = String.raw`(?:[^\[\]\\]|\\.|\[(?:[^\[\]\\]|\\.)*\])*`;
// The destination (angle-bracketed, or with balanced parentheses) and an optional title.
const LINK_TAIL = String.raw`\]\(\s*(<[^>\n]*>|(?:[^\s()]|\([^\s()]*\))*)((?:\s+(?:"[^"]*"|'[^']*'|\([^)]*\)))?\s*)\)`;
const INLINE_IMAGE = new RegExp(String.raw`!\[(${LINK_TEXT})${LINK_TAIL}`, 'g');
const INLINE_LINK = new RegExp(String.raw`(?<!!)\[(${LINK_TEXT})${LINK_TAIL}`, 'g');
const REFERENCE_DEFINITION = /^( {0,3}\[([^\]\n]+)\]:[ \t]*)(<[^>\n]*>|\S+)/gm;
const AUTOLINK = /<([a-z][a-z0-9+.-]{1,31}:[^\s<>]*)>/gi;

const unwrap = (target: string) => (target.startsWith('<') && target.endsWith('>') ? target.slice(1, -1) : target);
const normalizeLabel = (label: string) => label.trim().replace(/\s+/g, ' ').toLowerCase();

/** Labels of reference definitions that some image uses (`![alt][label]`, `![label][]`, `![label]`). */
function imageReferenceLabels(tokens: TokensList): Set<string> {
  const labels = new Set<string>();
  marked.walkTokens(tokens, (token) => {
    if (token.type !== 'image' || token.raw.includes('](')) return;
    const match = new RegExp(String.raw`^!\[(${LINK_TEXT})\](?:\[([^\]]*)\])?$`).exec(token.raw);
    if (match) labels.add(normalizeLabel(match[2] || match[1] || ''));
  });
  return labels;
}

/** Image and link tokens the parser still sees with a target outside the allowlist, and raw HTML. */
function unsafeTokens(markdown: string): Array<{ raw: string; replacement: string }> {
  const found: Array<{ raw: string; replacement: string }> = [];
  marked.walkTokens(marked.lexer(markdown), (token) => {
    if (token.type === 'image' && token.href !== '#' && safeImageSrc(token.href) === null) {
      found.push({ raw: token.raw, replacement: `![${token.text.replace(/[[\]]/g, '')}](#)` });
    } else if (token.type === 'link' && safeHref(token.href) === null) {
      found.push({
        raw: token.raw,
        replacement: token.raw.startsWith('<') ? '#' : `[${token.text.replace(/[[\]]/g, '')}](#)`,
      });
    } else if (token.type === 'html') {
      // Shown as text, as the HTML renderer does: a Markdown client must not render it either.
      found.push({ raw: token.raw, replacement: token.raw.replaceAll('<', '&lt;') });
    }
  });
  return found;
}

/**
 * Markdown for agents (the .md twins and llms files): comments removed, links
 * localized, and every link and image held to the same URL allowlist as the
 * HTML (./safe-url.ts), so a twin carries no script URL and no remote image a
 * Markdown client would fetch:
 *
 *   - inline images `![alt](target)` → `safeImageSrc`, inline links → `safeHref`
 *     (link text may nest brackets, e.g. an image inside a link);
 *   - reference definitions → `safeImageSrc` when an image uses the label,
 *     otherwise `safeHref`; autolinks → http(s)/mailto only;
 *   - raw HTML is escaped (the HTML renderer shows it as text too).
 *
 * The result is then parsed again with the same parser as the HTML; any image
 * or link it still finds outside the allowlist (deeper nesting, unusual
 * syntax) is rewritten to `#` and parsed again, so the guarantee does not rest
 * on the regular expressions alone.
 */
export function cleanMarkdown(markdown: string, locale: string): string {
  return sanitizeMarkdown(localizeMarkdownHrefs(stripComments(markdown), locale));
}

/**
 * Rewrites the root-relative destinations of LINKS (inline links and the
 * reference definitions links use) with `rewrite`; images are left alone, so
 * a local `/images/…` source stays root-relative and keeps passing
 * safeImageSrc. Used to make a twin's links absolute.
 */
export function rewriteRootRelativeLinks(markdown: string, rewrite: (href: string) => string): string {
  const imageLabels = imageReferenceLabels(marked.lexer(markdown));
  const rootRelative = (target: string) => target.startsWith('/') && !target.startsWith('//');
  return markdown
    .replace(INLINE_LINK, (match, text: string, target: string, rest: string) => {
      const href = unwrap(target);
      return rootRelative(href) ? `[${text}](${rewrite(href)}${rest})` : match;
    })
    .replace(REFERENCE_DEFINITION, (match, head: string, label: string, target: string) => {
      const href = unwrap(target);
      if (imageLabels.has(normalizeLabel(label)) || !rootRelative(href)) return match;
      return `${head}${rewrite(href)}`;
    });
}

/**
 * The URL allowlist and raw-HTML escaping of cleanMarkdown, for any Markdown
 * document. Also applied to every complete generated document (each twin,
 * llms.txt, llms-full.txt, llms-docs.txt), because those mix sanitized
 * bodies with CMS-editable titles, descriptions and FAQ text.
 */
export function sanitizeMarkdown(source: string): string {
  const imageLabels = imageReferenceLabels(marked.lexer(source));
  let output = source
    .replace(
      INLINE_IMAGE,
      (_match, text: string, target: string, rest: string) =>
        `![${text}](${safeImageSrc(unwrap(target)) ?? '#'}${rest})`,
    )
    .replace(
      INLINE_LINK,
      (_match, text: string, target: string, rest: string) => `[${text}](${safeHref(unwrap(target)) ?? '#'}${rest})`,
    )
    .replace(REFERENCE_DEFINITION, (_match, head: string, label: string, target: string) => {
      const safe = imageLabels.has(normalizeLabel(label)) ? safeImageSrc(unwrap(target)) : safeHref(unwrap(target));
      return `${head}${safe ?? '#'}`;
    })
    .replace(AUTOLINK, (_match, target: string) => {
      const safe = safeHref(target);
      return safe && /^(https?|mailto):/i.test(safe) ? `<${safe}>` : '#';
    });

  for (let round = 0; ; round += 1) {
    const unsafe = unsafeTokens(output);
    if (unsafe.length === 0) break;
    if (round === 10) throw new Error(`sanitizeMarkdown: could not neutralize ${unsafe[0]!.raw}`);
    for (const { raw, replacement } of unsafe) output = output.split(raw).join(replacement);
  }
  return output.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Plain text (a title, a description) made safe to place inside Markdown:
 * every character with Markdown meaning is backslash-escaped, so the text
 * reads the same and cannot open a link, an image, emphasis or HTML.
 */
export function escapeMarkdownText(text: string): string {
  return text.replace(/\s+/g, ' ').replace(/[\\`*_[\]<>!|~]/g, (character) => `\\${character}`);
}
